"use client";
import React, { useState } from "react";
import { Phone, AlertCircle, CheckCircle } from "lucide-react";
import { Input } from "../ui/input";
import config from "@nusrif/config";
interface MobileInputProps {
  value: string;
  onChange: (value: string) => void;
  isValid: boolean;
}
export const MobileInput = ({
  value,
  onChange,
  isValid,
}: MobileInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // Remove all non-digit characters (except + for country code)
    const cleaned = inputValue.replace(/[^\d+]/g, "");
    // If starts with +, handle country code separately
    let countryCode = "";
    let number = cleaned;
    if (cleaned.startsWith("+")) {
      countryCode = cleaned.slice(0, 3); // e.g., +91
      number = cleaned.slice(3);
    }
    // Limit the number part to config.region.mobileLength digits
    number = number.replace(/\D/g, "").slice(0, config.region.mobileLength);
    // Reconstruct the input
    const limitedInput = countryCode + number;
    // Do not format in view; keep raw limited input
    onChange(limitedInput);
  };
  const getValidationMessage = (): string | null => {
    if (!value.trim() || isFocused) return null;
    const digitsOnly = value.replace(/\D/g, "");
    if (digitsOnly.length < config.region.mobileLength) {
      return `Mobile number must be at least ${config.region.mobileLength} digits`;
    } else if (digitsOnly.length > config.region.mobileLength) {
      return`Mobile number cannot exceed ${config.region.mobileLength} digits`;
    }
    return isValid ? null : "Please enter a valid mobile number";
  };
  const validationMessage = getValidationMessage();
  const hasError = validationMessage !== null;
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">Mobile Number</label>
      <div className="relative">
        {/* Left Icon */}
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
          <Phone className="w-5 h-5 text-gray-400" />
        </div>
        {/* Input Field */}
        <Input
          type="tel"
          value={value}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder="Enter your mobile number"
          className={`pl-12 pr-12 h-14 text-lg transition-all duration-200 ${
            hasError
              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
              : isValid && value.trim()
              ? "border-green-500 focus:border-green-500 focus:ring-green-500"
              : "border-gray-300 focus:border-primary-blue focus:ring-primary-blue"
          }`}
          autoComplete="tel"
          inputMode="tel"
        />
        {/* Right Icon (Validation) */}
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          {value.trim() &&
            (isValid ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-500" />
            ))}
        </div>
      </div>
      {/* Validation Message */}
      {validationMessage && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <AlertCircle className="w-4 h-4" />
          <span>{validationMessage}</span>
        </div>
      )}
      {/* Success Message */}
      {isValid && value.trim() && !isFocused && (
        <div className="flex items-center gap-2 text-sm text-green-600">
          <CheckCircle className="w-4 h-4" />
          <span>Valid mobile number</span>
        </div>
      )}
    </div>
  );
};
